// Torre de control v2 (docs/PLAN-TORRE-V2.md): gestion por excepcion.
// El operador no mira 40 trips verdes: mira las exceptions abiertas y las
// demoras sin avisar. Estos schemas son el wire format de ese flujo.
//
// PII minimizada por diseno: la nomina del trip expone record, nombre, stop
// y status; JAMAS domicilio ni phone (eso queda en el padron, fuera de la
// torre).
import { z } from "zod";
import { Coordinates, Integer, Instant, ProviderId, RecordId, StopId, SiteId, TripId, LocalTime, RideRequestId, Decimal, } from "./common.js";
import { OperatingDate, OptOutReason, RideRequestTravelDirection } from "./ride-requests.js";
import { AssignmentExceptionType, TripResponse } from "./trips.js";
import { DocumentId, DocumentSubject, DriverId, IneligibilityReason, LocalVehicleId } from "./drivers.js";
// --- Excepciones operativas --------------------------------------------------
export const ExceptionId = z.string().min(1).openapi("ExceptionId", {
    description: "Identificador de una excepcion operativa de la torre.",
    example: "exc_trip_20260911_shuttle-01_demora_umbral",
});
export const ExceptionType = z
    .enum([
    "delay_threshold",
    "handover_risk",
    "provider_no_departure",
    "no_signal",
    "mobility_pending",
])
    .openapi("ExceptionType", {
    description: "Tipo de excepcion operativa: demora sobre umbral (E1), riesgo de no llegar al relevo (E2), el proveedor no salio (E11), sin senal GPS (E4) o excepcion de movilidad pendiente (E10, solo visibilidad).",
});
export const ExceptionSeverity = z.enum(["info", "warning", "critical"]).openapi("ExceptionSeverity", {
    description: "Severidad de la excepcion: ordena la cola de la torre.",
});
// Nombre distinto de WaiverStatus (exceptions.ts, flujo de movilidad):
// este es el ciclo de vida de la excepcion OPERATIVA de la torre.
// expired (en castellano: vencida) lo pone solo el barrido del cierre diario
// (PLAN-TORRE-V3-NOVEDADES §3), nunca POST /resolve: por eso no exige motivo.
export const ExceptionStatus = z
    .enum(["detected", "acknowledged", "in_progress", "resolved", "expired"])
    .openapi("ExceptionStatus", {
    description: "Ciclo de vida de la excepcion operativa: detected -> acknowledged -> in_progress -> resolved. expired es terminal y lo asigna el cierre diario a lo que quedo abierto (resolvedBy 'sistema', sin motivo de catalogo).",
});
export const ReasonCatalog = z
    .enum([
    "traffic",
    "weather",
    "breakdown",
    "road_closure",
    "provider_cause",
    "false_alarm",
    "other",
])
    .openapi("ReasonCatalog", {
    description: "Catalogo cerrado de motivos: obligatorio al resolver una excepcion y causa del aviso de demora. Cerrado a proposito: la metrica solo agrega si el motivo es comparable.",
});
const exceptionBase = {
    exceptionId: ExceptionId,
    siteId: SiteId,
    tripId: TripId,
    severity: ExceptionSeverity,
    status: ExceptionStatus,
    detectedAt: Instant,
    acknowledgedBy: z.string().min(1).optional().openapi({
        description: "Email del operador que reconocio la excepcion.",
    }),
    acknowledgedAt: Instant.optional(),
    resolvedBy: z.string().min(1).optional().openapi({
        description: "Email del operador que la resolvio, o 'sistema'.",
    }),
    resolvedAt: Instant.optional(),
    resolutionReason: ReasonCatalog.optional().openapi({
        description: "Motivo de catalogo, obligatorio al resolver.",
    }),
    dataIssue: z.enum(["missing", "invalid"]).optional(),
};
export const DelayThresholdData = z
    .object({
    delayMin: Integer.min(0),
    thresholdMin: Integer.min(0),
    reason: z.string(),
    eta: Instant.optional(),
}).strict().openapi("DelayThresholdData", { description: "Demora observada y umbral que disparo la excepcion. El motivo es texto operativo, no el catalogo de resolucion." });
export const HandoverRiskData = z
    .object({
    eta: Instant,
    plannedArrival: Instant,
    marginMin: Integer.min(0),
    affectedPeople: Integer.min(0),
}).strict().openapi("HandoverRiskData", { description: "Llegada proyectada y planificada, margen y cantidad de personas afectadas." });
export const ProviderNoDepartureData = z
    .object({
    plannedDeparture: Instant,
    minutesWithoutDeparture: Integer.min(0),
}).strict().openapi("ProviderNoDepartureData", { description: "Salida planificada y minutos transcurridos sin salir." });
export const NoSignalData = z
    .object({
    lastPositionAt: Instant.optional(),
    minutesWithoutSignal: Integer.min(0).optional(),
}).strict().openapi("NoSignalData", { description: "Ultima senal y minutos sin senal; ambos ausentes cuando nunca hubo posicion." });
export const DelayThresholdException = z
    .object({
    ...exceptionBase,
    type: z.literal("delay_threshold"),
    data: DelayThresholdData.optional(),
}).strict().openapi("DelayThresholdException", { description: "Excepcion operativa de demora sobre umbral, con contexto propio del tipo." });
export const DelayThresholdContext = z
    .object({
    type: z.literal("delay_threshold"),
    severity: ExceptionSeverity.optional(),
    data: DelayThresholdData.optional(),
    dataIssue: z.enum(["missing", "invalid"]).optional(),
}).strict().openapi("DelayThresholdContext", { description: "Contexto historico de demora sobre umbral; dataIssue declara contexto faltante o invalido." });
export const HandoverRiskException = z
    .object({
    ...exceptionBase,
    type: z.literal("handover_risk"),
    data: HandoverRiskData.optional(),
}).strict().openapi("HandoverRiskException", { description: "Excepcion operativa de riesgo de relevo, con contexto propio del tipo." });
export const HandoverRiskContext = z
    .object({
    type: z.literal("handover_risk"),
    severity: ExceptionSeverity.optional(),
    data: HandoverRiskData.optional(),
    dataIssue: z.enum(["missing", "invalid"]).optional(),
}).strict().openapi("HandoverRiskContext", { description: "Contexto historico de riesgo de relevo; dataIssue declara contexto faltante o invalido." });
export const ProviderNoDepartureException = z
    .object({
    ...exceptionBase,
    type: z.literal("provider_no_departure"),
    data: ProviderNoDepartureData.optional(),
}).strict().openapi("ProviderNoDepartureException", { description: "Excepcion operativa de proveedor sin salida, con contexto propio del tipo." });
export const ProviderNoDepartureContext = z
    .object({
    type: z.literal("provider_no_departure"),
    severity: ExceptionSeverity.optional(),
    data: ProviderNoDepartureData.optional(),
    dataIssue: z.enum(["missing", "invalid"]).optional(),
}).strict().openapi("ProviderNoDepartureContext", { description: "Contexto historico de proveedor sin salida; dataIssue declara contexto faltante o invalido." });
export const NoSignalException = z
    .object({
    ...exceptionBase,
    type: z.literal("no_signal"),
    data: NoSignalData.optional(),
}).strict().openapi("NoSignalException", { description: "Excepcion operativa de ausencia de senal, con contexto propio del tipo." });
export const NoSignalContext = z
    .object({
    type: z.literal("no_signal"),
    severity: ExceptionSeverity.optional(),
    data: NoSignalData.optional(),
    dataIssue: z.enum(["missing", "invalid"]).optional(),
}).strict().openapi("NoSignalContext", { description: "Contexto historico de ausencia de senal; dataIssue declara contexto faltante o invalido." });
export const MobilityPendingException = z
    .object({
    ...exceptionBase,
    type: z.literal("mobility_pending"),
}).strict().openapi("MobilityPendingException", { description: "Excepcion operativa de excepcion de movilidad pendiente, con contexto propio del tipo." });
export const MobilityPendingContext = z
    .object({
    type: z.literal("mobility_pending"),
    severity: ExceptionSeverity.optional(),
    dataIssue: z.enum(["missing", "invalid"]).optional(),
}).strict().openapi("MobilityPendingContext", { description: "Contexto historico de excepcion de movilidad pendiente; dataIssue declara contexto faltante o invalido." });
// --- Novedades de la asignacion (PP-33, PLAN-ASIGNACION-PROVEEDOR §8) ----------
// Desde 0.6.15 (AT-7 / PP-35) son variantes de OperationalException y tipos de
// AlertType: la torre las lista, reconoce y resuelve como cualquier novedad.
// ExceptionType sigue siendo solo lo que detecta el motor del tick.
export const OfferId = Integer.min(1).openapi("OfferId", {
    description: "Numero de la oferta dentro de la cadena del viaje (1 es la primera). Obligatorio en aceptar y rechazar: vuelve condicional la operacion.",
    example: 1,
});
export const AssignmentExceptionData = z
    .object({
    offerSeq: OfferId.optional(),
    providerId: ProviderId,
    elapsedSeconds: Integer.min(0).optional().openapi({ description: "Segundos desde que se ofrecio el viaje a ese proveedor." }),
    ineligibilityReasons: z.array(IneligibilityReason).optional(),
})
    .strict()
    .openapi("AssignmentExceptionData", { description: "Contexto de la novedad: proveedor que tenia la oferta, tiempo transcurrido y motivos de elegibilidad. Nunca titular, tercerizacion ni ids globales." });
const assignmentException = (type, name, description) => z.object({ ...exceptionBase, type: z.literal(type), data: AssignmentExceptionData.optional() }).strict().openapi(name, { description });
export const AssignmentExpiredException = assignmentException("assignment_expired", "AssignmentExpiredException", "Novedad de asignacion: vencio el plazo de aceptar o de asignar y el viaje se reofrecio.");
export const AssignmentQueueExhaustedException = assignmentException("assignment_queue_exhausted", "AssignmentQueueExhaustedException", "Novedad de asignacion: se agoto la cola de proveedores; la planta cancela o relanza.");
export const AssignmentIneligibleException = assignmentException("assignment_ineligible", "AssignmentIneligibleException", "Novedad de asignacion: el proveedor asigno un vehiculo o conductor no elegible (ADR-014 §2), con los motivos tipados.");
export const AssignmentChangedInProgressException = assignmentException("assignment_changed_in_progress", "AssignmentChangedInProgressException", "Novedad de asignacion historica: cambio del par asignado con el viaje en curso (no se emite desde PP-34b).");
export const AssignmentMissingInProgressException = assignmentException("assignment_missing_in_progress", "AssignmentMissingInProgressException", "Novedad de asignacion: el viaje salio sin vehiculo ni conductor asignados.");
export const OperationalException = z.discriminatedUnion("type", [
    DelayThresholdException,
    HandoverRiskException,
    ProviderNoDepartureException,
    NoSignalException,
    MobilityPendingException,
    AssignmentExpiredException,
    AssignmentQueueExhaustedException,
    AssignmentIneligibleException,
    AssignmentChangedInProgressException,
    AssignmentMissingInProgressException,
]).openapi("OperationalException", { description: "Excepcion operativa discriminada por tipo: las del motor (ExceptionType) y, desde 0.6.15, las novedades de la asignacion (AssignmentExceptionType). Contexto ausente o invalido se declara con dataIssue, sin inventar valores." });
export const ExceptionContext = z.discriminatedUnion("type", [
    DelayThresholdContext,
    HandoverRiskContext,
    ProviderNoDepartureContext,
    NoSignalContext,
    MobilityPendingContext,
]).openapi("ExceptionContext", { description: "Contexto tipado de la excepcion detectada en el historial; puede conservar ausencias historicas." });
export const ExceptionsResponse = z
    .object({
    exceptions: z.array(OperationalException).openapi({
        description: "Excepciones de la planta: abiertas primero (por severity y antiguedad), resueltas del dia al final.",
    }),
})
    .openapi("ExceptionsResponse", { description: "Cola de excepciones de la torre." });
export const ExceptionParams = z
    .object({ id: ExceptionId })
    .openapi("ExceptionParams", { description: "Parametro de path :id de una excepcion operativa." });
// --- Avisos de demora ----------------------------------------------------------
export const DelayNoticeId = z.string().min(1).openapi("DelayNoticeId", {
    description: "Identificador de un aviso de demora.",
    example: "aviso_trip_20260911_shuttle-01_demora_umbral",
});
export const NoticeParams = z
    .object({ id: DelayNoticeId })
    .openapi("NoticeParams", { description: "Parametro de path :id de un aviso de demora." });
export const NoticeStatus = z
    .enum(["draft", "sent", "updated", "closed", "vetoed", "expired"])
    .openapi("NoticeStatus", {
    description: "Maquina de estados del aviso: draft -> sent -> updated -> closed; terminales vetoed y expired (borrador que nadie envio y el viaje llego).",
});
// PB-4b: origen del aviso (delay_notices.origin, migracion 0023).
export const NoticeOrigin = z
    .enum(["detected", "driver_report", "demo"])
    .openapi("NoticeOrigin", {
    description: "Quien origino el aviso. detected: el motor de excepciones (y toda fila anterior a 0.6.8). driver_report: el conductor asignado desde la app mobile. demo: la demora a demanda del tenant demo (POST /tower/demo/delay). Separa la evidencia real de la demo.",
});
export const NoticeClassification = z
    .enum(["notified_on_time", "notified_late", "not_notified"])
    .openapi("NoticeClassification", {
    description: "Clasificacion final de la demora, sellada al cerrar el aviso. La metrica solo vale si es honesta: un borrador nunca enviado es una demora NO avisada.",
});
export const StopEta = z
    .object({
    stopId: StopId,
    plannedTime: Instant.openapi({
        description: "Hora planificada original de la parada, en ISO 8601 UTC.",
    }),
    eta: Instant.openapi({
        description: "ETA recalculada con la demora activa, en ISO 8601 UTC.",
    }),
})
    .openapi("StopEta", { description: "ETA recalculada de una parada afectada por la demora." });
export const DelayNotice = z
    .object({
    noticeId: DelayNoticeId,
    siteId: SiteId,
    tripId: TripId,
    exceptionId: ExceptionId.optional().openapi({
        description: "Excepcion que origino el borrador automatico, si la hubo.",
    }),
    status: NoticeStatus,
    origin: NoticeOrigin.optional().openapi({
        description: "Origen del aviso. La API lo envia siempre desde 0.6.8; opcional solo para compatibilidad de clientes generados antes.",
    }),
    delayMin: Integer.positive().openapi({
        description: "Minutos de demora que el aviso comunica.",
        example: 12,
    }),
    cause: ReasonCatalog,
    etaByStop: z.array(StopEta).openapi({
        description: "ETA recalculada por parada no cumplida al momento del aviso.",
    }),
    createdAt: Instant,
    sentAt: Instant.optional(),
    sentBy: z.string().min(1).optional().openapi({
        description: "Email del operador que confirmo el envio.",
    }),
    closedAt: Instant.optional(),
    classification: NoticeClassification.optional().openapi({
        description: "Presente solo cuando el aviso esta en un estado terminal.",
    }),
})
    .openapi("DelayNotice", {
    description: "Aviso de demora: el flujo estrella de la torre v2. E1/E2/E11 generan el borrador automatico; el operador confirma el envio con un click.",
});
export const NoticesResponse = z
    .object({ notices: z.array(DelayNotice) })
    .openapi("NoticesResponse", { description: "Avisos de demora del dia de la planta." });
// --- Novedades (docs/PLAN-TORRE-V3-NOVEDADES.md) --------------------------------
// en castellano: novedad. Una novedad es una proyeccion de lectura sobre
// excepciones y avisos, sin tabla propia: la union lleva el objeto completo
// adentro para que las tarjetas conserven countdown y acciones (los endpoints
// de reconocer, resolver, enviar y vetar siguen siendo los de cada fuente).
export const AlertId = z
    .string()
    .regex(/^(exc|ntc|doc):.+$/)
    .openapi("AlertId", {
    description: "Identificador de una novedad: 'exc:<exceptionId>', 'ntc:<noticeId>' o, desde 0.6.31, desde 0.6.36 'doc:<documentAlertId>' (registro de documento vencido; entre 0.6.31 y 0.6.35 era 'doc:<documentId>/<providerId>/<id local>'). El prefijo coincide con source.",
    example: "exc:exc_trip_20260911_shuttle-01_demora_umbral",
});
export const AlertSource = z.enum(["exception", "delay_notice", "document"]).openapi("AlertSource", {
    description: "Fuente de la novedad: una excepcion operativa, un aviso de demora o, desde 0.6.31, un documento vencido de la flota de la planta. Es el discriminador de Alert.",
});
export const AlertType = z
    .enum([...ExceptionType.options, "delay_notice", ...AssignmentExceptionType.options, "document_expired"])
    .openapi("AlertType", {
    description: "Tipo de novedad: los tipos de excepcion operativa del motor, delay_notice (aviso de demora; su origin distingue el detectado, el del conductor y el de la demo), desde 0.6.15 las novedades de la asignacion y, desde 0.6.31, document_expired (documento vencido que la planta exige). Es la clave del filtro por tipo de la configuracion del panel.",
});
export const AlertStatus = z
    .enum(["open", "acknowledged", "in_progress", "resolved", "expired"])
    .openapi("AlertStatus", {
    description: "Estado unificado de la novedad. Excepcion: detected -> open, el resto igual. Aviso: draft -> open, sent y updated -> in_progress, closed y vetoed -> resolved, expired -> expired.",
});
const alertBase = {
    alertId: AlertId,
    type: AlertType,
    severity: ExceptionSeverity.openapi({
        description: "Severidad que ordena el panel. Para un aviso: warning en borrador (demora sin avisar), info una vez enviado.",
    }),
    status: AlertStatus,
    tripId: TripId,
    detectedAt: Instant.openapi({
        description: "Cuando aparecio la novedad (detectedAt de la excepcion o createdAt del aviso). El panel compara estos instantes entre si para contar no leidas.",
    }),
    expiresAt: Instant.optional().openapi({
        description: "Hora a partir de la cual el cierre diario la puede vencer: plannedArrival del viaje si se conoce, si no las 00:00 del dia operativo siguiente en la zona del site. No dispara nada por si sola. Ausente si el viaje no tiene fecha operativa determinable.",
    }),
    tripDate: OperatingDate.optional().openapi({
        description: "Desde 0.6.38: fecha operativa del viaje en la zona de la planta, para que la tarjeta diga de que dia es cuando no es de hoy. Una novedad abierta de un viaje de un dia anterior no llega al panel; una cerrada hoy si. Ausente si el viaje no tiene fecha operativa determinable.",
    }),
};
export const ExceptionAlert = z
    .object({
    ...alertBase,
    source: z.literal("exception"),
    exception: OperationalException,
})
    .strict()
    .openapi("ExceptionAlert", {
    description: "Novedad originada en una excepcion operativa; lleva la excepcion completa para reconocer y resolver desde la tarjeta.",
});
export const NoticeAlert = z
    .object({
    ...alertBase,
    source: z.literal("delay_notice"),
    notice: DelayNotice,
})
    .strict()
    .openapi("NoticeAlert", {
    description: "Novedad originada en un aviso de demora; lleva el aviso completo para enviar o vetar desde la tarjeta.",
});
// AT-7, decision D-5 (03/10): el documento vencido es novedad de la torre de
// la planta y de UCORP en la consola; el portal del proveedor no la recibe
// (el proveedor ya ve el estado de cada documento en su flota). Desde 0.6.36
// (AT-7b, decision "registro" del 03/10) es un registro persistido con
// historia: lo abre y lo resuelve el cierre diario, una persona lo reconoce.
export const DocumentExpiredAlertData = z
    .object({
    documentId: DocumentId,
    documentType: z.string().min(1).openapi({
        description: "Tipo de documento vencido (catalogo abierto de la planta: vtv, seguro, licencia...). Uno que la planta exigia al abrirse la novedad.",
        example: "vtv",
    }),
    expiresAt: OperatingDate.openapi({
        description: "Fecha de vencimiento del documento al abrirse la novedad (calendario). Desde el dia siguiente, en la zona de la planta, el documento esta vencido.",
    }),
    subject: DocumentSubject,
})
    .strict()
    .openapi("DocumentExpiredAlertData", {
    description: "Documento vencido nombrado desde la planta: tipo, vencimiento y el sujeto con su id local. Nunca titular, tercerizacion, referencia del documento ni ids globales.",
});
export const DocumentAlertId = z.string().min(1).openapi("DocumentAlertId", {
    description: "Identificador opaco del registro de documento vencido (AT-7b). Lo genera el cierre diario; el alertId de la novedad es 'doc:' seguido de este valor.",
    example: "dal-3f9a1c2b",
});
export const DocumentAlertStatus = z.enum(["open", "acknowledged", "resolved"]).openapi("DocumentAlertStatus", {
    description: "Estado del registro de documento vencido (subconjunto de AlertStatus): open (abierta por el cierre diario), acknowledged (una persona de la planta o de UCORP la reconocio; sigue abierta mientras el documento siga vencido) y resolved (el cierre diario la cerro, con resolution). No vence sola: dura lo que dure la causa.",
});
export const DocumentAlertResolution = z
    .enum(["renewed", "removed", "blocked", "not_required"])
    .openapi("DocumentAlertResolution", {
    description: "Por que el cierre diario cerro la novedad (AT-7b): renewed (el documento se renovo o lo reemplazo uno vigente del mismo tipo), removed (en castellano: baja; el sujeto salio de la nomina o el documento se borro sin reemplazo), blocked (bloqueo de la planta, proveedor deshabilitado o cadena de ADR-014 §4 inactiva) o not_required (la planta dejo de exigir ese tipo).",
});
export const DocumentAlert = z
    .object({
    alertId: AlertId,
    source: z.literal("document"),
    type: z.literal("document_expired"),
    documentAlertId: DocumentAlertId.openapi({
        description: "Id del registro (desde 0.6.36): es el que lleva POST /sites/{siteId}/documents/alerts/{documentAlertId}/acknowledge.",
    }),
    severity: ExceptionSeverity.openapi({
        description: "Severidad que ordena el panel. Hoy siempre warning: la unidad deja de ser elegible para la planta.",
    }),
    status: DocumentAlertStatus,
    detectedAt: Instant.openapi({
        description: "Cuando vencio el documento: 00:00 del dia siguiente a expiresAt, en la zona de la planta. Es el instante con el que el panel cuenta no leidas.",
    }),
    acknowledgedBy: z.string().min(1).optional().openapi({
        description: "Email de quien la reconocio (operador de la planta o de UCORP). Ausente si nadie la reconocio.",
    }),
    acknowledgedAt: Instant.optional().openapi({ description: "Cuando se reconocio. Ausente si nadie la reconocio." }),
    resolvedAt: Instant.optional().openapi({
        description: "Cuando la cerro el cierre diario (status resolved). Ausente mientras siga abierta.",
    }),
    resolution: DocumentAlertResolution.optional().openapi({
        description: "Por que se cerro. Presente si y solo si status es resolved.",
    }),
    document: DocumentExpiredAlertData,
})
    .strict()
    .openapi("DocumentAlert", {
    description: "Novedad de documento vencido (AT-7, D-5; registro desde AT-7b): un vehiculo o chofer habilitado y activo en la planta tiene vencido un documento que la planta exige, y por eso dejo de ser elegible. La abre y la resuelve el cierre diario, nunca una lectura. Sin viaje asociado: el clic lleva a la flota.",
});
export const DocumentAlertParams = z
    .object({ siteId: SiteId, documentAlertId: DocumentAlertId })
    .openapi("DocumentAlertParams", { description: "Planta y registro de documento vencido del path." });
export const AcknowledgeDocumentAlertResponse = z
    .object({ alert: DocumentAlert })
    .openapi("AcknowledgeDocumentAlertResponse", {
    description: "La novedad de documento vencido despues de reconocerla. Reconocer dos veces no cambia autor ni hora originales.",
});
export const Alert = z.discriminatedUnion("source", [ExceptionAlert, NoticeAlert, DocumentAlert]).openapi("Alert", {
    description: "Novedad de la torre (en castellano: novedad), discriminada por source. Vista unificada de excepciones, avisos y, desde 0.6.31, documentos vencidos (registro persistido desde 0.6.36); no se borra, vence sola o se resuelve cuando se corrige su causa.",
});
export const AlertsResponse = z
    .object({
    alerts: z.array(Alert).openapi({
        description: "Novedades abiertas de la planta, por severidad, countdown y antiguedad; despues las resueltas y vencidas del dia operativo de hoy por resolvedAt descendente.",
    }),
    serverTime: Instant.optional().openapi({
        description: "Hora del servidor al responder (0.8.3, AT-24). La torre estima con ella el desvio del reloj del navegador y cuenta la espera y su vencimiento con la hora del servidor, la misma que decide el no-show. Va en el cuerpo porque el header Date no se expone entre origenes.",
    }),
})
    .openapi("AlertsResponse", { description: "Panel de novedades de la torre." });
export const DocumentAlertsResponse = z
    .object({
    today: OperatingDate.openapi({
        description: "Fecha operativa de la planta al momento de la lectura. Desde 0.6.36 los registros los abre el cierre diario, no esta lectura.",
    }),
    alerts: z.array(DocumentAlert).openapi({
        description: "Registros de documento vencido de la planta: primero los abiertos y reconocidos, por vencimiento mas antiguo; despues el historial de resueltos, por resolvedAt descendente.",
    }),
})
    .openapi("DocumentAlertsResponse", {
    description: "Novedades de documento vencido de una planta con su historia, para la consola de UCORP (AT-7, D-5, AT-7b). La torre recibe en /tower/alerts las abiertas y las resueltas hoy.",
});
// --- Ficha de trip --------------------------------------------------------------
export const TripPassengerStatus = z
    .enum(["assigned", "onboard", "no_show", "opted_out"])
    .openapi("TripPassengerStatus", {
    description: "Estado del pasajero en el viaje: assigned (su reserva ya tiene vehiculo puesto por el armado), onboard (subio en su parada), no_show (no se presento sin avisar) u opted_out (aviso 'hoy no viajo' antes del viaje: su lugar en la parada queda liberado). no_show y opted_out son metricas distintas del cierre diario. La reserva la inicia siempre el pasajero, en combi y en colectivo por igual.",
});
export const TripPassenger = z
    .object({
    record: RecordId,
    name: z.string().min(1).openapi({
        description: "Nombre del pasajero, tal como figura en el padron.",
        example: "Ana Suarez",
    }),
    stopId: StopId,
    status: TripPassengerStatus,
})
    .openapi("TripPassenger", {
    description: "Pasajero de la nomina del viaje. PII minimizada: SIN domicilio ni telefono; la torre no los necesita y no los recibe.",
});
const tripEventBase = { id: Integer, tripId: TripId, instant: Instant, author: z.string().min(1) };
export const DelayDetail = z
    .object({
    delayMin: Integer.min(0),
    reason: z.string(),
    origin: z.literal("on_demand").optional(),
}).strict().openapi("DelayDetail", { description: "Detalle tipado del evento de demora." });
export const DelayEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("delay"),
    detail: DelayDetail,
}).strict().openapi("DelayEvent", { description: "Evento de demora en el historial del viaje." });
export const ExceptionDetectedDetail = z
    .object({
    exceptionId: ExceptionId,
    exception: ExceptionContext,
}).strict().openapi("ExceptionDetectedDetail", { description: "Detalle tipado del evento de deteccion de excepcion." });
export const ExceptionDetectedEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("exception_detected"),
    detail: ExceptionDetectedDetail,
}).strict().openapi("ExceptionDetectedEvent", { description: "Evento de deteccion de excepcion en el historial del viaje." });
export const ExceptionAcknowledgedDetail = z
    .object({
    exceptionId: ExceptionId,
}).strict().openapi("ExceptionAcknowledgedDetail", { description: "Detalle tipado del evento de reconocimiento de excepcion." });
export const ExceptionAcknowledgedEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("exception_acknowledged"),
    detail: ExceptionAcknowledgedDetail,
}).strict().openapi("ExceptionAcknowledgedEvent", { description: "Evento de reconocimiento de excepcion en el historial del viaje." });
export const ExceptionResolvedDetail = z
    .object({
    exceptionId: ExceptionId,
    resolutionReason: ReasonCatalog,
    detail: z.string().optional(),
}).strict().openapi("ExceptionResolvedDetail", { description: "Detalle tipado del evento de resolucion de excepcion." });
export const ExceptionResolvedEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("exception_resolved"),
    detail: ExceptionResolvedDetail,
}).strict().openapi("ExceptionResolvedEvent", { description: "Evento de resolucion de excepcion en el historial del viaje." });
export const NoticeDraftDetail = z
    .object({
    noticeId: DelayNoticeId,
    delayMin: Integer.min(0),
    cause: ReasonCatalog.optional(),
}).strict().openapi("NoticeDraftDetail", { description: "Detalle tipado del evento de borrador de aviso." });
export const NoticeDraftEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("notice_draft"),
    detail: NoticeDraftDetail,
}).strict().openapi("NoticeDraftEvent", { description: "Evento de borrador de aviso en el historial del viaje." });
export const NoticeSentDetail = z
    .object({
    noticeId: DelayNoticeId,
    delayMin: Integer.min(0),
}).strict().openapi("NoticeSentDetail", { description: "Detalle tipado del evento de envio de aviso." });
export const NoticeSentEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("notice_sent"),
    detail: NoticeSentDetail,
}).strict().openapi("NoticeSentEvent", { description: "Evento de envio de aviso en el historial del viaje." });
export const NoticeVetoedDetail = z
    .object({
    noticeId: DelayNoticeId,
    reason: ReasonCatalog,
}).strict().openapi("NoticeVetoedDetail", { description: "Detalle tipado del evento de veto de aviso." });
export const NoticeVetoedEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("notice_vetoed"),
    detail: NoticeVetoedDetail,
}).strict().openapi("NoticeVetoedEvent", { description: "Evento de veto de aviso en el historial del viaje." });
export const NoticeClosedDetail = z
    .object({
    noticeId: DelayNoticeId,
    classification: NoticeClassification,
}).strict().openapi("NoticeClosedDetail", { description: "Detalle tipado del evento de cierre de aviso." });
export const NoticeClosedEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("notice_closed"),
    detail: NoticeClosedDetail,
}).strict().openapi("NoticeClosedEvent", { description: "Evento de cierre de aviso en el historial del viaje." });
export const NoticeExpiredDetail = z
    .object({
    noticeId: DelayNoticeId,
    classification: z.literal("not_notified"),
}).strict().openapi("NoticeExpiredDetail", { description: "Detalle tipado del evento de vencimiento de aviso." });
export const NoticeExpiredEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("notice_expired"),
    detail: NoticeExpiredDetail,
}).strict().openapi("NoticeExpiredEvent", { description: "Evento de vencimiento de aviso en el historial del viaje." });
// Los dos eventos siguientes los escribe solo el barrido del cierre diario
// (PLAN-TORRE-V3-NOVEDADES §3), con autor 'sistema'.
export const ExceptionExpiredDetail = z
    .object({
    exceptionId: ExceptionId,
    // PP-33: el cierre diario tambien vence novedades de la asignacion
    // (salvo la cola agotada, PLAN-ASIGNACION-PROVEEDOR §12.2).
    type: z.union([ExceptionType, AssignmentExceptionType]),
}).strict().openapi("ExceptionExpiredDetail", { description: "Detalle tipado del evento de vencimiento de excepcion (en castellano: excepcion vencida): la excepcion quedo abierta y el cierre diario la vencio." });
export const ExceptionExpiredEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("exception_expired"),
    detail: ExceptionExpiredDetail,
}).strict().openapi("ExceptionExpiredEvent", { description: "Evento de vencimiento de excepcion en el historial del viaje." });
export const TripClosedByDailyCloseDetail = z
    .object({
    previousStatus: z.enum(["scheduled", "in_progress"]).openapi({
        description: "Estado en que el barrido encontro el viaje antes de cancelarlo.",
    }),
}).strict().openapi("TripClosedByDailyCloseDetail", { description: "Detalle tipado del evento de viaje cerrado por el cierre diario: un viaje sin telemetria de llegada no opero; se cancela, no se completa." });
export const TripClosedByDailyCloseEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("trip_closed_by_daily_close"),
    detail: TripClosedByDailyCloseDetail,
}).strict().openapi("TripClosedByDailyCloseEvent", { description: "Evento de viaje cerrado por el cierre diario en el historial del viaje." });
export const RoutingDetail = z
    .object({
    // G9c (0.6.24): un viaje de traslado libre deja point_to_point.
    direction: RideRequestTravelDirection.openapi({ description: "Sentido de las solicitudes del viaje: inbound, outbound o point_to_point (traslado libre en auto, 0.6.24: el viaje no pasa por la porteria)." }),
    batch: Integer.min(0),
    rideRequests: z.array(RideRequestId),
}).strict().openapi("RoutingDetail", { description: "Detalle tipado del evento de armado de viajes." });
export const RoutingEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("routing"),
    detail: RoutingDetail,
}).strict().openapi("RoutingEvent", { description: "Evento de armado de viajes en el historial del viaje." });
export const PassengerOptedOutDetail = z
    .object({
    requestId: RideRequestId,
    record: RecordId,
    stopId: StopId.optional(),
    reason: OptOutReason,
    comment: z.string().max(200).optional(),
    remainingPassengersAtStop: Integer.min(0),
    skippableStop: z.boolean(),
}).strict().openapi("PassengerOptedOutDetail", { description: "Detalle tipado del evento de aviso de pasajero que no viaja." });
export const PassengerOptedOutEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("passenger_opted_out"),
    detail: PassengerOptedOutDetail,
}).strict().openapi("PassengerOptedOutEvent", { description: "Evento de aviso de pasajero que no viaja en el historial del viaje." });
export const HistoricalEventDetail = z
    .object({
    originalType: z.string(),
    issue: z.enum(["unknown_type", "invalid_detail"]),
}).strict().openapi("HistoricalEventDetail", { description: "Tipo original y causa de incompatibilidad de una fila historica, sin exponer su contenido libre." });
export const HistoricalEvent = z
    .object({
    ...tripEventBase,
    type: z.literal("historical_event"),
    detail: HistoricalEventDetail,
}).strict().openapi("HistoricalEvent", { description: "Proyeccion de lectura para historia desconocida o invalida. Nunca se acepta en una escritura." });
// --- Aviso y progreso del conductor (PB-4b) -------------------------------------
// Catalogo PROVISORIO y minimo: el definitivo no esta decidido. Aislado en un
// solo schema para poder cambiarlo; su valor se copia tal cual a la causa del
// aviso (subconjunto de ReasonCatalog), sin motivo va 'other'.
export const MobileDelayReason = z.enum(["traffic", "breakdown", "road_closure", "weather", "other"]).openapi("MobileDelayReason", {
    description: "Motivo opcional de la demora informada por el conductor. Catalogo provisorio y minimo, sujeto a cambio; subconjunto de ReasonCatalog.",
});
export const MOBILE_DELAY_COMMENT_MAX = 140;
// Historial del progreso del conductor en trip_events (PB-4b). Viven aca y
// no en mobile-trips.ts porque desde 0.6.15 (AT-7 / PP-35) forman parte de
// TripEvent: la ficha de la torre los muestra. Las escrituras se siguen
// validando con NewDriverTripEvent, nunca con NewTripEvent.
export const DriverTripStartedDetail = z.object({ driverId: DriverId, vehicleId: LocalVehicleId.optional() }).strict().openapi("DriverTripStartedDetail", { description: "El conductor asignado marco la salida." });
export const DriverStopArrivedDetail = z.object({ driverId: DriverId, stopId: z.string().min(1), order: Integer.min(0) }).strict().openapi("DriverStopArrivedDetail", { description: "El conductor marco la llegada a una parada." });
export const DriverTripCompletedDetail = z.object({ driverId: DriverId }).strict().openapi("DriverTripCompletedDetail", { description: "El conductor marco el fin del viaje." });
export const DriverDelayReportedDetail = z.object({
    driverId: DriverId, noticeId: DelayNoticeId, delayMin: Integer.min(1),
    reason: MobileDelayReason.optional(), comment: z.string().max(MOBILE_DELAY_COMMENT_MAX).optional(),
}).strict().openapi("DriverDelayReportedDetail", { description: "El conductor informo una demora; el aviso queda en borrador en la torre." });
// ADR-029 (0.8.0): abordaje o no-show de un pasajero marcado por el conductor.
// El legajo va porque la torre ya lo ve en la nomina; la app del conductor
// nunca lo recibe (trabaja con passengerRef).
export const DriverBoardingStatus = z.enum(["onboard", "no_show"]).openapi("DriverBoardingStatus", {
    description: "Resultado del abordaje que marca el conductor: onboard (subio) o no_show (no se presento, solo vencida la tolerancia de la planta).",
});
export const DriverPassengerBoardedDetail = z.object({
    driverId: DriverId, stopId: z.string().min(1), order: Integer.min(0), record: RecordId, status: DriverBoardingStatus,
}).strict().openapi("DriverPassengerBoardedDetail", { description: "El conductor marco que un pasajero subio o no se presento en su parada de subida." });
const driverEventBase = { id: Integer, tripId: TripId, instant: Instant, author: z.string().min(1) };
export const DriverTripStartedEvent = z.object({ ...driverEventBase, type: z.literal("driver_trip_started"), detail: DriverTripStartedDetail }).strict().openapi("DriverTripStartedEvent", { description: "Salida marcada por el conductor (scheduled a in_progress)." });
export const DriverStopArrivedEvent = z.object({ ...driverEventBase, type: z.literal("driver_stop_arrived"), detail: DriverStopArrivedDetail }).strict().openapi("DriverStopArrivedEvent", { description: "Llegada a parada marcada por el conductor." });
export const DriverTripCompletedEvent = z.object({ ...driverEventBase, type: z.literal("driver_trip_completed"), detail: DriverTripCompletedDetail }).strict().openapi("DriverTripCompletedEvent", { description: "Fin marcado por el conductor (in_progress a completed)." });
export const DriverDelayReportedEvent = z.object({ ...driverEventBase, type: z.literal("driver_delay_reported"), detail: DriverDelayReportedDetail }).strict().openapi("DriverDelayReportedEvent", { description: "Demora informada por el conductor." });
export const DriverPassengerBoardedEvent = z.object({ ...driverEventBase, type: z.literal("driver_passenger_boarded"), detail: DriverPassengerBoardedDetail }).strict().openapi("DriverPassengerBoardedEvent", { description: "Abordaje o no-show marcado por el conductor (0.8.0). instant es la hora del hecho." });
export const DriverTripEvent = z.discriminatedUnion("type", [DriverTripStartedEvent, DriverStopArrivedEvent, DriverTripCompletedEvent, DriverDelayReportedEvent, DriverPassengerBoardedEvent]).openapi("DriverTripEvent", { description: "Evento append-only del conductor sobre su viaje; author es el sub verificado del token mobile." });
export const NewDriverTripEvent = z.discriminatedUnion("type", [
    DriverTripStartedEvent.omit({ id: true }), DriverStopArrivedEvent.omit({ id: true }),
    DriverTripCompletedEvent.omit({ id: true }), DriverDelayReportedEvent.omit({ id: true }),
    DriverPassengerBoardedEvent.omit({ id: true }),
]).openapi("NewDriverTripEvent", { description: "Evento nuevo del conductor validado antes de persistir; el id lo asigna la base." });
export const TripEventType = z.enum(["delay", "exception_detected", "exception_acknowledged", "exception_resolved", "exception_expired", "notice_draft", "notice_sent", "notice_vetoed", "notice_closed", "notice_expired", "routing", "passenger_opted_out", "trip_closed_by_daily_close", "historical_event", "driver_trip_started", "driver_stop_arrived", "driver_trip_completed", "driver_delay_reported", "driver_passenger_boarded"]).openapi("TripEventType", { description: "Tipos canonicos de evento. En los driver_* instant es la hora del hecho que mando la app (occurredAt, 0.8.0) o, sin ella, la de recepcion. historical_event es exclusivamente una proyeccion de lectura; exception_expired y trip_closed_by_daily_close los escribe solo el cierre diario. Los driver_* los escribe el conductor asignado desde mobile (la ficha de la torre proyecta su autor como 'conductor') y se validan con NewDriverTripEvent." });
export const TripEvent = z.discriminatedUnion("type", [
    DelayEvent,
    ExceptionDetectedEvent,
    ExceptionAcknowledgedEvent,
    ExceptionResolvedEvent,
    ExceptionExpiredEvent,
    NoticeDraftEvent,
    NoticeSentEvent,
    NoticeVetoedEvent,
    NoticeClosedEvent,
    NoticeExpiredEvent,
    TripClosedByDailyCloseEvent,
    RoutingEvent,
    PassengerOptedOutEvent,
    HistoricalEvent,
    DriverTripStartedEvent,
    DriverStopArrivedEvent,
    DriverTripCompletedEvent,
    DriverDelayReportedEvent,
    DriverPassengerBoardedEvent,
]).openapi("TripEvent", { description: "Evento del historial append-only. Una fila historica incompatible conserva identidad, fecha y actor." });
export const NewDelayEvent = DelayEvent.omit({ id: true }).openapi("NewDelayEvent", { description: "Evento nuevo de demora, sin identificador de base." });
export const NewExceptionDetectedEvent = ExceptionDetectedEvent.omit({ id: true }).openapi("NewExceptionDetectedEvent", { description: "Evento nuevo de deteccion de excepcion, sin identificador de base." });
export const NewExceptionAcknowledgedEvent = ExceptionAcknowledgedEvent.omit({ id: true }).openapi("NewExceptionAcknowledgedEvent", { description: "Evento nuevo de reconocimiento de excepcion, sin identificador de base." });
export const NewExceptionResolvedEvent = ExceptionResolvedEvent.omit({ id: true }).openapi("NewExceptionResolvedEvent", { description: "Evento nuevo de resolucion de excepcion, sin identificador de base." });
export const NewNoticeDraftEvent = NoticeDraftEvent.omit({ id: true }).openapi("NewNoticeDraftEvent", { description: "Evento nuevo de borrador de aviso, sin identificador de base." });
export const NewNoticeSentEvent = NoticeSentEvent.omit({ id: true }).openapi("NewNoticeSentEvent", { description: "Evento nuevo de envio de aviso, sin identificador de base." });
export const NewNoticeVetoedEvent = NoticeVetoedEvent.omit({ id: true }).openapi("NewNoticeVetoedEvent", { description: "Evento nuevo de veto de aviso, sin identificador de base." });
export const NewNoticeClosedEvent = NoticeClosedEvent.omit({ id: true }).openapi("NewNoticeClosedEvent", { description: "Evento nuevo de cierre de aviso, sin identificador de base." });
export const NewNoticeExpiredEvent = NoticeExpiredEvent.omit({ id: true }).openapi("NewNoticeExpiredEvent", { description: "Evento nuevo de vencimiento de aviso, sin identificador de base." });
export const NewExceptionExpiredEvent = ExceptionExpiredEvent.omit({ id: true }).openapi("NewExceptionExpiredEvent", { description: "Evento nuevo de vencimiento de excepcion, sin identificador de base." });
export const NewTripClosedByDailyCloseEvent = TripClosedByDailyCloseEvent.omit({ id: true }).openapi("NewTripClosedByDailyCloseEvent", { description: "Evento nuevo de viaje cerrado por el cierre diario, sin identificador de base." });
export const NewRoutingEvent = RoutingEvent.omit({ id: true }).openapi("NewRoutingEvent", { description: "Evento nuevo de armado de viajes, sin identificador de base." });
export const NewPassengerOptedOutEvent = PassengerOptedOutEvent.omit({ id: true }).openapi("NewPassengerOptedOutEvent", { description: "Evento nuevo de aviso de pasajero que no viaja, sin identificador de base." });
export const NewTripEvent = z.discriminatedUnion("type", [
    NewDelayEvent,
    NewExceptionDetectedEvent,
    NewExceptionAcknowledgedEvent,
    NewExceptionResolvedEvent,
    NewExceptionExpiredEvent,
    NewNoticeDraftEvent,
    NewNoticeSentEvent,
    NewNoticeVetoedEvent,
    NewNoticeClosedEvent,
    NewNoticeExpiredEvent,
    NewTripClosedByDailyCloseEvent,
    NewRoutingEvent,
    NewPassengerOptedOutEvent,
]).openapi("NewTripEvent", { description: "Evento nuevo canonico validado antes de persistir. Excluye historical_event; el id lo asigna la base." });
export const TripDetailResponse = TripResponse.extend({
    passengers: z.array(TripPassenger).openapi({
        description: "Nomina del viaje con estado de abordaje. Con capacidad habilita el n/N de la torre.",
    }),
    events: z.array(TripEvent).openapi({
        description: "Eventos recientes del viaje, mas nuevos primero.",
    }),
    expiredDocuments: z.boolean().optional().openapi({
        description: "true si el vehiculo o el chofer asignados tienen vencido algun documento de los tipos que la planta exige, a su fecha operativa (R1.5: alerta en torre; ADR-014: el documento es uno solo por auto o persona). Ausente cuando el viaje no tiene unidad ni chofer asignados o el repositorio no evalua documentacion (modo memoria).",
    }),
}).openapi("TripDetailResponse", {
    description: "Ficha completa de un viaje para el panel derecho de la torre: TripResponse + etaDestination + paradas con completedAt + nomina + capacidad + vehiculo + historial reciente.",
});
// --- Acciones de escritura de la torre (ola 2) --------------------------------
// Toda escritura corre en el scope de negocio (tenant del token), exige rol de
// torre (site_admin o site_operador) y deja su evento en eventos_de_trip.
export const AcknowledgeExceptionRequest = z.object({}).openapi("AcknowledgeExceptionRequest", {
    description: "Body de reconocer una excepcion: vacio a proposito. La identidad del operador sale del token, jamas del body. Idempotente: reconocer dos veces no cambia el author original.",
});
export const AcknowledgeExceptionResponse = z
    .object({ exception: OperationalException })
    .openapi("AcknowledgeExceptionResponse", {
    description: "La excepcion actualizada tras reconocerla.",
});
export const ResolveExceptionRequest = z
    .object({
    reason: ReasonCatalog.openapi({
        description: "Motivo de catalogo, OBLIGATORIO al resolver.",
    }),
    detail: z.string().min(1).optional().openapi({
        description: "Texto libre opcional que acompana al motivo en el evento.",
    }),
})
    .openapi("ResolveExceptionRequest", {
    description: "Resolver una excepcion abierta: el motivo del catalogo cerrado es obligatorio (la metrica solo agrega si el motivo es comparable).",
});
export const ResolveExceptionResponse = z
    .object({ exception: OperationalException })
    .openapi("ResolveExceptionResponse", {
    description: "La excepcion actualizada tras resolverla.",
});
export const SendNoticeRequest = z.object({}).openapi("SendNoticeRequest", {
    description: "Body de enviar un aviso de demora: vacio a proposito. El texto ya esta pre-armado en el borrador; el operador solo confirma con un click.",
});
export const SendNoticeResponse = z
    .object({ notice: DelayNotice })
    .openapi("SendNoticeResponse", { description: "El aviso actualizado tras confirmar el envio." });
export const VetoNoticeRequest = z
    .object({
    reason: ReasonCatalog.openapi({
        description: "Motivo de catalogo por el que se veta el borrador.",
    }),
})
    .openapi("VetoNoticeRequest", {
    description: "Vetar un borrador de aviso (falsa alarma, demora ya salvada): estado terminal vetado, con motivo obligatorio.",
});
export const VetoNoticeResponse = z
    .object({ notice: DelayNotice })
    .openapi("VetoNoticeResponse", { description: "El aviso actualizado tras vetarlo." });
export const DelayOnDemandRequest = z
    .object({
    tripId: TripId,
    delayMin: Integer.min(5).max(60).openapi({
        description: "Minutos de demora a aplicar (5 a 60).",
        example: 15,
    }),
    reason: ReasonCatalog,
})
    .openapi("DelayOnDemandRequest", {
    description: "Demora a demanda (SOLO tenant demo, jamas en prod): provoca una demora en vivo para narrar en reuniones. El proximo tick del generador la propaga en excepcion y borrador de aviso.",
});
export const DelayOnDemandResponse = z
    .object({ trip: TripResponse })
    .openapi("DelayOnDemandResponse", { description: "El viaje actualizado con la demora aplicada." });
export const TripItineraryResponse = z
    .object({
    tripId: TripId,
    itinerary: z.array(Coordinates).openapi({
        description: "Polilinea por calles del viaje completo. Inmutable: se sirve con Cache-Control largo, fuera del polling de 5 segundos.",
    }),
})
    .openapi("TripItineraryResponse", {
    description: "Recorrido de un viaje, servido solo: el payload pesado e inmutable sale del polling.",
});
// --- Planta para la torre (R1.5: la torre no hardcodea el tenant) -------------
export const TowerShift = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1).openapi({ example: "Manana" }),
    startTime: LocalTime.openapi({ description: "Hora local de ingreso: el relevo que la torre cuenta." }),
    endTime: LocalTime,
})
    .openapi("TowerShift", { description: "Un turno de la planta, tal como lo declara el manifiesto." });
export const TowerLocation = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    coordinates: Coordinates,
})
    .openapi("TowerLocation", { description: "La sede principal: el marcador PLANTA del mapa." });
export const TowerOperatingArea = z
    .object({
    latMin: Decimal,
    latMax: Decimal,
    lngMin: Decimal,
    lngMax: Decimal,
})
    .openapi("TowerOperatingArea", { description: "Rectangulo de la operacion: encuadre inicial del mapa." });
export const TowerPolicy = z
    .object({
    maxJourneyMin: Integer.min(0),
    handoverMarginMin: Integer.min(0).openapi({
        description: "Minutos de tolerancia para 'llega a tiempo': la unica definicion, compartida con el cierre diario.",
    }),
})
    .openapi("TowerPolicy", { description: "Parametros de la politica del tenant que la torre muestra." });
export const TowerSiteResponse = z
    .object({
    siteId: SiteId,
    name: z.string().min(1).openapi({ description: "Nombre de uso diario de la planta (encabezado de la torre)." }),
    timezone: z.string().min(1).openapi({
        description: "Zona IANA de la planta: los relevos y las fechas operativas se calculan en ella, nunca en la del navegador.",
        example: "America/Argentina/Buenos_Aires",
    }),
    location: TowerLocation,
    operatingArea: TowerOperatingArea,
    shifts: z.array(TowerShift).min(1),
    policy: TowerPolicy,
})
    .openapi("TowerSiteResponse", {
    description: "Lo minimo del tenant que la torre necesita pintar: nombre, sede, area, turnos y politica. Sin personas ni proveedores.",
});
//# sourceMappingURL=tower.js.map